import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateChartOfAccount, useChartOfAccounts, ACCOUNT_TYPE_LABEL, AccountType } from "@/api/accounting";
import { getApiErrorMessage } from "@/api/client";

const ACCOUNT_TYPE_OPTIONS = Object.keys(ACCOUNT_TYPE_LABEL) as AccountType[];

export default function CreateChartOfAccountModal({ onClose }: { onClose: () => void }) {
  const createAccount = useCreateChartOfAccount();
  const { data: parentOptions } = useChartOfAccounts({ pageSize: 200 });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("ACTIVO");
  const [parentId, setParentId] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createAccount.mutateAsync({
        code,
        name,
        accountType,
        parentId: parentId || undefined,
        description: description || undefined,
      });
      toast.success("Cuenta contable creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nueva cuenta contable</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Código</label>
              <input className="input-field mt-1" placeholder="Ej. 1105" value={code} onChange={(e) => setCode(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo</label>
              <select className="input-field mt-1" value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType)}>
                {ACCOUNT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {ACCOUNT_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" placeholder="Ej. Caja" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cuenta padre (opcional)</label>
            <select className="input-field mt-1" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">Sin cuenta padre</option>
              {parentOptions?.data.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Descripción</label>
            <textarea className="input-field mt-1" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createAccount.isPending}>
              {createAccount.isPending ? "Creando..." : "Crear cuenta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
